import React, {forwardRef, useEffect, useRef} from 'react'
import styled from 'styled-components'
import {useRefObjectAsForwardedRef} from '@fuegokit/react/hooks'
import sx from '@fuegokit/react/sx'
import {RuleBuilderStepsProvider, useRuleBuilderStepsContext} from './context/RuleBuilderStepsContext'
import {RuleBuilderStepProps} from './RuleBuilderStep'
import {RuleBuilderStepsProps} from './types'

// rely on the native semantics of the <li> element and handle keyboard navigation within the step's content.
const OrderedList = styled.ol<RuleBuilderStepsProps>`
  margin: 0;
  padding: 0;
  text-indent: 0;
  list-style-type: none;
  list-style: none;

  flex-direction: column;
  text-align: center;
  width: 100%;
  display: flex;
  flex: 1;

  // there's margin-collapse on the last step
  /* > * + * {
    margin-block-start: 12px;
  } */
  ${sx}
`
/** A set of steps that describes the progression through or the steps in a workflow */
const RuleBuilderSteps = forwardRef<HTMLOListElement, RuleBuilderStepsProps>(
  (
    {children, ariaLabel, activeStep = 0, initialStep = 0, state, onSelectStep, sx: sxProp, testId, ...props},
    forwardedRef
  ) => {
    // const {activeStep, nextStep, prevStep, setActiveStep, reset, isSelected, setIsSelected} = useRuleBuilderSteps({
    //   initialStep
    // })
    const childArr = React.Children.toArray(children)

    const containerRef = React.useRef(null) as React.RefObject<HTMLOListElement>
    useRefObjectAsForwardedRef(forwardedRef, containerRef)

    const [stepCount, setStepCount] = React.useState(childArr.length)
    const [selectedStepIndex, setSelectedStepIndex] = React.useState<number | null>(initialStep || 0)
    const {isLoading, isError} = useRuleBuilderStepsContext()
    const stepRefs = useRef<(HTMLLIElement | null)[]>([])

    // syncs stepRefs with correct number of steps
    useEffect(() => {
      stepRefs.current = stepRefs.current.slice(0, stepCount)
    }, [stepCount])

    // keyboard navigation
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        // Tab key
        if (event.key === 'Tab') {
          if (containerRef.current && containerRef.current.contains(document.activeElement)) {
            event.preventDefault()
            // console.log(`you pressed ${event.key}`)

            const activeIndex = stepRefs.current.findIndex(ref => ref === document.activeElement)
            const nextIndex = event.shiftKey ? activeIndex - 1 : activeIndex + 1
            const numSteps = React.Children.count(children)

            if (activeIndex !== -1) {
              const isValidIndex = nextIndex >= 0 && nextIndex < numSteps

              if (isValidIndex) {
                if (onSelectStep) onSelectStep(nextIndex)
                stepRefs.current[nextIndex]?.focus()
              } else if (event.shiftKey && activeIndex === 0) {
                // Shift + Tab from first Step, focus on last Step
                if (onSelectStep) onSelectStep(numSteps - 1)
                stepRefs.current[numSteps - 1]?.focus()
              } else if (!event.shiftKey && activeIndex === numSteps - 1) {
                const lastStep = stepRefs.current[numSteps - 1]
                if (lastStep instanceof HTMLElement) {
                  lastStep.blur()
                  const nextFocusableElement = containerRef.current.nextElementSibling
                  if (nextFocusableElement instanceof HTMLElement) {
                    nextFocusableElement.focus()
                  }
                }
              }
            }
          }
        }
      }
      window.addEventListener('keydown', handleKeyDown)
      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }, [selectedStepIndex, activeStep, onSelectStep, stepCount])

    return (
      <RuleBuilderStepsProvider value={{activeStep, state, stepCount, onSelectStep}}>
        <OrderedList aria-label={ariaLabel} stepCount={stepCount} ref={containerRef} sx={sxProp}>
          {React.Children.map(children, (child, i) => {
            const stepRef = (element: HTMLLIElement | null) => {
              stepRefs.current[i] = element
            }

            const isLastStep = i === stepCount - 1
            const isSecondToLastStep = i === stepCount - 2
            const isSelectedStep = i === activeStep

            const stepProps = {
              index: i,
              isLastStep,
              isSecondToLastStep,
              isSelectedStep,
              stepRef: (element: HTMLLIElement | null) => {
                stepRefs.current[i] = element
              }
            }

            if (React.isValidElement(child)) {
              return React.cloneElement(child as React.ReactElement<RuleBuilderStepProps>, stepProps)
            }

            return null
          })}
        </OrderedList>
      </RuleBuilderStepsProvider>
    )
  }
)

RuleBuilderSteps.displayName = 'RuleBuilderSteps'
export default RuleBuilderSteps
